import { supabase } from './supabase';
import { TeamMember, UserRole } from '../types';
import { toCamel } from './database';
import { normalizeStoredUploadUrl } from './urlNormalize';

// ============================================================================
// PHASE 3 ADAPTERS
// These dual-compatibility functions currently interact with the legacy schema 
// (e.g., allowed_workspaces array on allowed_users).
// In Phase 5, they will be flipped to query the new `workspace_users` join table.
// ============================================================================

export async function fetchTeamMembersByWorkspace(workspaceId: string): Promise<TeamMember[]> {
    // Phase 5: Query the workspace_users join table and map the user relationship
    const { data, error } = await supabase.from('workspace_users')
        .select(`
            role,
            allowed_views,
            workspace_id,
            user_id,
            user:allowed_users ( id, email, name, date_added, photo_url, client_id )
        `)
        .eq('workspace_id', workspaceId);

    // Throw on error so React Query surfaces isError + retries, instead of caching a
    // silent empty roster that looks identical to "no team members".
    if (error) throw new Error(error.message);
    if (!data) return [];
    
    return data.map((row: any) => ({
        id: row.user?.id || row.user_id,
        email: row.user?.email || '',
        name: row.user?.name || '',
        role: row.role as UserRole,
        allowedViews: row.allowed_views || [],
        dateAdded: row.user?.date_added || new Date().toISOString(),
        // Heal legacy 1-hour signed URLs into permanent public ones on READ (no
        // DB write) so employee photos stop vanishing after their TTL.
        photoUrl: normalizeStoredUploadUrl(row.user?.photo_url),
        clientId: row.user?.client_id,
        // Also keep workspaceId linked just in case UI needs it
        workspaceId: row.workspace_id
    })) as TeamMember[];
}

export async function fetchAllPlatformUsers() {
    // Phase 5: Fetch all users and manually join their workspaces to reconstruct the allowed_workspaces array
    // Since SuperAdmin is still using `user.allowed_workspaces` for display and routing
    const { data: users, error: usersErr } = await supabase.from('allowed_users').select('*').order('name', { ascending: true });
    if (usersErr) throw new Error(usersErr.message);
    if (!users) return [];

    const { data: rels, error: relsErr } = await supabase.from('workspace_users').select('user_id, workspace_id, role');
    if (relsErr) throw new Error(relsErr.message);
    
    return users.map(u => {
        const myRels = (rels || []).filter(r => r.user_id === u.id);
        const workspaces = myRels.map(r => r.workspace_id);
        const myRole = myRels.length > 0 ? myRels[0].role : u.role;
        return {
            ...u,
            role: myRole, // override with specific role if needed, or fallback
            allowed_workspaces: workspaces
        };
    });
}

export async function fetchAllWorkspaces() {
    // Fetches all workspaces (relied upon by WorkspaceContext and SuperAdminView)
    const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .order('created_at', { ascending: true });
        
    return { data, error };
}
